import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType, User, LoginRequest, LoginWithOTPRequest, SignupRequest } from '../types/auth';
import { AuthAPI, NotificationsAPI } from '../services/api';
import { requestNotificationPermission } from '../firebase';

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [needsTwoFactorSetup, setNeedsTwoFactorSetup] = useState(false);
  const [signupUserEmail, setSignupUserEmail] = useState<string | null>(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('access_token');
        const storedRefreshToken = localStorage.getItem('refresh_token');
        
        if (storedToken) {
          setToken(storedToken);
          setRefreshToken(storedRefreshToken);
          AuthAPI.setAuthToken(storedToken);
          
          // Try to get user info
          try {
            const userData = await AuthAPI.getMe();
            setUser(userData);
          } catch (error: any) {
            // Check if session expired due to inactivity
            if (error.message === 'SESSION_INACTIVE_LOGOUT') {
              clearAuthState();
              sessionStorage.setItem('logoutReason', 'inactivity');
              return;
            }

            // Token might be expired, try to refresh
            if (storedRefreshToken) {
              try {
                const newTokenData = await AuthAPI.refreshToken(storedRefreshToken);
                const newToken = newTokenData.access_token;
                setToken(newToken);
                localStorage.setItem('access_token', newToken);
                AuthAPI.setAuthToken(newToken);

                // Retry getting user info
                const userData = await AuthAPI.getMe();
                setUser(userData);
              } catch (refreshError: any) {
                // Check if refresh failed due to inactivity
                if (refreshError.message === 'SESSION_INACTIVE_LOGOUT') {
                  sessionStorage.setItem('logoutReason', 'inactivity');
                }
                // Refresh failed, clear everything
                clearAuthState();
              }
            } else {
              clearAuthState();
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuthState();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const clearAuthState = () => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    setRequires2FA(false);
    setNeedsTwoFactorSetup(false);
    setSignupUserEmail(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    AuthAPI.removeAuthToken();
  };

  // Register FCM token for push notifications
  const registerForPushNotifications = async () => {
    try {
      const fcmToken = await requestNotificationPermission();
      if (fcmToken) {
        await NotificationsAPI.registerFCMToken(fcmToken, navigator.userAgent);
        console.log('FCM token registered successfully');
      }
    } catch (error) {
      console.warn('Failed to register FCM token:', error);
      // Don't fail login if FCM registration fails
    }
  };

  const login = async (credentials: LoginRequest) => {
    try {
      setLoading(true);
      console.log('AuthContext: Starting login for:', credentials.username);
      const tokenData = await AuthAPI.login(credentials);
      
      console.log('AuthContext: Login successful, setting tokens');
      setToken(tokenData.access_token);
      if (tokenData.refresh_token) {
        setRefreshToken(tokenData.refresh_token);
        localStorage.setItem('refresh_token', tokenData.refresh_token);
      }
      localStorage.setItem('access_token', tokenData.access_token);
      AuthAPI.setAuthToken(tokenData.access_token);
      
      // Get user data
      const userData = await AuthAPI.getMe();
      setUser(userData);
      setRequires2FA(false);

      // Register for push notifications after successful login
      registerForPushNotifications();
    } catch (error: any) {
      console.log('AuthContext: Login error:', error.message);
      if (error.message === '2FA_REQUIRED') {
        console.log('AuthContext: Setting 2FA required state');
        setRequires2FA(true);
        // Store credentials for OTP form
        localStorage.setItem('temp_username', credentials.username);
        localStorage.setItem('temp_password', credentials.password);
        throw error;
      }
      console.log('AuthContext: Throwing error:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithOTP = async (credentials: LoginWithOTPRequest) => {
    try {
      setLoading(true);
      const tokenData = await AuthAPI.loginWithOTP(credentials);
      
      setToken(tokenData.access_token);
      if (tokenData.refresh_token) {
        setRefreshToken(tokenData.refresh_token);
        localStorage.setItem('refresh_token', tokenData.refresh_token);
      }
      localStorage.setItem('access_token', tokenData.access_token);
      AuthAPI.setAuthToken(tokenData.access_token);
      
      // Get user data
      const userData = await AuthAPI.getMe();
      setUser(userData);
      setRequires2FA(false);

      // Clear temporary credentials
      localStorage.removeItem('temp_username');
      localStorage.removeItem('temp_password');

      // Register for push notifications after successful login
      registerForPushNotifications();
    } finally {
      setLoading(false);
    }
  };

  const signup = async (data: SignupRequest) => {
    try {
      setLoading(true);
      const userData = await AuthAPI.signup(data);
      
      // After successful signup, automatically log the user in
      // so they can access the 2FA setup endpoint
      const loginResponse = await AuthAPI.login({
        username: data.username,
        password: data.password,
        remember_me: false
      });
      
      // Set tokens for authenticated requests
      setToken(loginResponse.access_token);
      if (loginResponse.refresh_token) {
        setRefreshToken(loginResponse.refresh_token);
        localStorage.setItem('refresh_token', loginResponse.refresh_token);
      }
      localStorage.setItem('access_token', loginResponse.access_token);
      AuthAPI.setAuthToken(loginResponse.access_token);
      
      // Set user data but require 2FA setup
      setUser(userData);
      setSignupUserEmail(data.email);
      setNeedsTwoFactorSetup(true);
      
    } catch (error: any) {
      // If login fails due to 2FA requirement, that's expected for new users
      if (error.message !== '2FA_REQUIRED') {
        throw error;
      }
      
      // New user doesn't have 2FA set up yet, so this is expected
      // We'll need to handle this differently - maybe auto-enable 2FA during signup
      setSignupUserEmail(data.email);
      setNeedsTwoFactorSetup(true);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await AuthAPI.logout();
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      clearAuthState();
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    loginWithOTP,
    signup,
    logout,
    loading,
    requires2FA,
    setRequires2FA,
    needsTwoFactorSetup,
    setNeedsTwoFactorSetup,
    signupUserEmail,
    setSignupUserEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};